/**
 * MusicMatch AI Service
 * Dynamic playlist based on BPM, mood, rhythm, and context
 */

import { logger } from '@/lib/logger';
import { supabase } from '@/integrations/supabase/client';

interface MusicContext {
  bpm?: number;
  pace?: number; // min/km
  mood: 'energized' | 'calm' | 'motivated' | 'focused';
  timeOfDay: 'morning' | 'afternoon' | 'evening' | 'night';
  weather?: 'sunny' | 'rainy' | 'cloudy';
  routeType?: 'urban' | 'trail' | 'track';
}

interface Track {
  id: string;
  name: string;
  artist: string;
  bpm: number;
  energy: number; // 0-1
  valence?: number; // 0-1 (positivity)
  danceability?: number; // 0-1
  duration: number; // seconds
  provider?: 'spotify' | 'apple' | 'deezer';
  uri?: string;
  preview_url?: string; // Spotify 30s preview
  album_art?: string; // Album cover URL
  spotify_uri?: string; // spotify:track:xxx
  external_url?: string; // Open in Spotify app/web
}

interface PlaylistRecommendation {
  tracks: Track[];
  reason: string;
  targetBPM: number;
}

class MusicMatchService {
  private currentContext: MusicContext | null = null;
  private currentTrack: Track | null = null;
  private playlist: Track[] = [];
  private provider: 'spotify' | 'apple' | 'deezer' = 'spotify';

  private moodTranslations: Record<string, string> = {
    energized: 'energizada',
    calm: 'calma',
    motivated: 'motivada',
    focused: 'focada'
  };

  private timeTranslations: Record<string, string> = {
    morning: 'matinal',
    afternoon: 'vespertina',
    evening: 'noturna',
    night: 'noturna'
  };

  private weatherTranslations: Record<string, string> = {
    sunny: 'ensolarado',
    rainy: 'chuvoso',
    cloudy: 'nublado'
  };

  async initialize(provider: 'spotify' | 'apple' | 'deezer' = 'spotify') {
    this.provider = provider;
    
    // TODO: Initialize provider SDK
    // For Spotify: await SpotifySDK.initialize()
    // For Apple Music: await AppleMusic.authorize()
    
    this.trackEvent('music_service_initialized', { provider });
  }

  async generatePlaylist(context: MusicContext): Promise<PlaylistRecommendation> {
    this.currentContext = context;

    // Calculate target BPM
    const targetBPM = this.calculateTargetBPM(context);

    // Get recommendations from AI or provider
    const tracks = await this.getRecommendations(targetBPM, context);

    this.playlist = tracks;

    const reason = this.generateReason(context, targetBPM);

    this.trackEvent('playlist_generated', {
      trackCount: tracks.length,
      targetBPM,
      mood: context.mood
    });

    return { tracks, reason, targetBPM };
  }

  private calculateTargetBPM(context: MusicContext): number {
    // If BPM (heart rate) available, use it as primary source
    if (context.bpm && context.bpm > 0) {
      return context.bpm;
    }

    // If pace available, estimate optimal music BPM from running cadence
    if (context.pace && context.pace > 0) {
      // Research-based cadence estimation:
      // Elite runners (< 4 min/km): ~185 SPM
      // Fast pace (4-5 min/km): ~175-180 SPM
      // Moderate (5-6 min/km): ~165-170 SPM
      // Easy (6-7 min/km): ~155-160 SPM
      // Recovery (> 7 min/km): ~145-155 SPM
      
      let estimatedCadence: number;
      if (context.pace < 4) {
        estimatedCadence = 185;
      } else if (context.pace < 4.5) {
        estimatedCadence = 180;
      } else if (context.pace < 5) {
        estimatedCadence = 175;
      } else if (context.pace < 5.5) {
        estimatedCadence = 170;
      } else if (context.pace < 6) {
        estimatedCadence = 165;
      } else if (context.pace < 6.5) {
        estimatedCadence = 160;
      } else if (context.pace < 7) {
        estimatedCadence = 155;
      } else if (context.pace < 8) {
        estimatedCadence = 150;
      } else {
        estimatedCadence = 140; // Walking pace
      }
      
      return estimatedCadence;
    }

    // Fallback by mood when no pace or HR available
    const moodBPM = {
      energized: 165,
      calm: 130,
      motivated: 155,
      focused: 145
    };

    return moodBPM[context.mood] || 150;
  }

  private async getRecommendations(targetBPM: number, context: MusicContext): Promise<Track[]> {
    try {
      // Try to get real tracks from Spotify first if provider is spotify
      if (this.provider === 'spotify') {
        const { data: spotifyData, error: spotifyError } = await supabase.functions.invoke('search-spotify-tracks', {
          body: {
            bpm: targetBPM,
            mood: context.mood,
            energy: 0.7,
            provider: 'spotify',
          }
        });

        if (!spotifyError && spotifyData?.tracks && spotifyData.tracks.length > 0) {
          logger.info('Found real tracks from Spotify', { 
            count: spotifyData.tracks.length,
            service: 'MusicMatch' 
          });
          return spotifyData.tracks;
        }
      }

      // Fallback to AI-generated playlist
      const { data, error } = await supabase.functions.invoke('generate-music-playlist', {
        body: context
      });

      if (error) {
        logger.error('Failed to generate AI playlist', error, { targetBPM, mood: context.mood });
        return [];
      }

      if (data?.tracks && Array.isArray(data.tracks)) {
        return data.tracks;
      }

      return [];
    } catch (error) {
      logger.error('Error getting AI recommendations', error, { targetBPM });
      return [];
    }
  }

  private generateReason(context: MusicContext, targetBPM: number): string {
    const reasons: string[] = [];

    if (context.bpm) {
      reasons.push(`Correspondendo à sua frequência cardíaca de ${context.bpm} BPM`);
    } else if (context.pace) {
      reasons.push(`Correspondendo ao seu ritmo de ${context.pace.toFixed(2)} min/km`);
    }

    const moodText = this.moodTranslations[context.mood] || context.mood;
    const timeText = this.timeTranslations[context.timeOfDay] || context.timeOfDay;
    reasons.push(`Energia ${moodText} para corrida ${timeText}`);

    if (context.weather) {
      const weatherText = this.weatherTranslations[context.weather] || context.weather;
      reasons.push(`Perfeito para clima ${weatherText}`);
    }

    return reasons.join(' + ');
  }

  async adjustPlaylist(newContext: MusicContext): Promise<Track | null> {
    if (!this.currentContext) return null;

    // Check if significant change in BPM/mood
    const bpmChange = newContext.bpm && this.currentContext.bpm
      ? Math.abs(newContext.bpm - this.currentContext.bpm)
      : 0;

    const moodChange = newContext.mood !== this.currentContext.mood;

    if (bpmChange > 10 || moodChange) {
      // Generate new track
      this.currentContext = newContext;
      const newTargetBPM = this.calculateTargetBPM(newContext);
      const tracks = await this.getRecommendations(newTargetBPM, newContext);
      
      if (tracks.length > 0) {
        const nextTrack = tracks[0];
        this.trackEvent('music_switch', {
          reason: bpmChange > 10 ? 'bpm_change' : 'mood_change',
          oldBPM: this.currentContext.bpm,
          newBPM: newContext.bpm,
          track: nextTrack.name
        });
        
        return nextTrack;
      }
    }

    return null;
  }

  setSpotifyPlayer(playFunction: (uri: string) => Promise<void>) {
    this.spotifyPlayFunction = playFunction;
  }

  private spotifyPlayFunction: ((uri: string) => Promise<void>) | null = null;

  async playTrack(track: Track): Promise<void> {
    this.currentTrack = track;
    
    // Use Spotify SDK if available
    if (this.provider === 'spotify' && track.spotify_uri && this.spotifyPlayFunction) {
      try {
        await this.spotifyPlayFunction(track.spotify_uri);
      } catch (error) {
        logger.error('Failed to play via Spotify SDK', error);
      }
    }
    
    this.trackEvent('track_played', {
      trackId: track.id,
      trackName: track.name,
      artist: track.artist,
      bpm: track.bpm
    });
  }

  async pause(): Promise<void> {
    // TODO: Call provider SDK
    this.trackEvent('playback_paused');
  }

  async resume(): Promise<void> {
    // TODO: Call provider SDK
    this.trackEvent('playback_resumed');
  }

  async next(): Promise<Track | null> {
    if (this.playlist.length === 0) return null;

    const currentIndex = this.playlist.findIndex(
      t => t.id === this.currentTrack?.id
    );

    const nextTrack = this.playlist[currentIndex + 1] || this.playlist[0];
    await this.playTrack(nextTrack);

    return nextTrack;
  }

  async previous(): Promise<Track | null> {
    if (this.playlist.length === 0) return null;

    const currentIndex = this.playlist.findIndex(
      t => t.id === this.currentTrack?.id
    );

    const prevTrack = this.playlist[currentIndex - 1] || this.playlist[this.playlist.length - 1];
    await this.playTrack(prevTrack);

    return prevTrack;
  }

  async likeTrack(track: Track): Promise<void> {
    this.trackEvent('music_like', {
      trackId: track.id,
      trackName: track.name
    });
  }

  async dislikeTrack(track: Track): Promise<void> {
    this.trackEvent('music_dislike', {
      trackId: track.id,
      trackName: track.name
    });
    
    // Skip to next
    await this.next();
  }

  getCurrentTrack(): Track | null {
    return this.currentTrack;
  }

  getPlaylist(): Track[] {
    return [...this.playlist];
  }

  private trackEvent(event: string, data?: any): void {
    logger.debug('MusicMatchService Event', { event, ...data });
    
    if (typeof window !== 'undefined' && (window as any).analytics) {
      (window as any).analytics.track(event, data);
    }
  }
}

export const musicMatchService = new MusicMatchService();
export type { MusicContext, Track, PlaylistRecommendation };
